/**
 * ConverterPage - Page de conversion de devises
 * @class ConverterPage
 */
import { getIcon } from '../utils/icons.js';
import { t, getCurrentLang } from '../utils/i18n.js';

export class ConverterPage {
    /**
     * Crée une instance de ConverterPage
     * @param {Object} options - Options de la page
     */
    constructor(options = {}) {
        this._services = options.services || {};
        this._state = options.state || {};
        this._onHistoryUpdate = options.onHistoryUpdate;
        this._container = null;
        this._currentConversion = null;
        this._cachedRate = { pair: '', rate: 0, timestamp: 0 }; // Cache simple pour optimisation
        
        // Devises communes
        this._commonCurrencies = [
            { code: 'EUR', name: 'Euro', nameEn: 'Euro' },
            { code: 'USD', name: 'Dollar américain', nameEn: 'US Dollar' },
            { code: 'GBP', name: 'Livre sterling', nameEn: 'British Pound' },
            { code: 'JPY', name: 'Yen japonais', nameEn: 'Japanese Yen' },
            { code: 'CHF', name: 'Franc suisse', nameEn: 'Swiss Franc' },
            { code: 'CAD', name: 'Dollar canadien', nameEn: 'Canadian Dollar' },
            { code: 'AUD', name: 'Dollar australien', nameEn: 'Australian Dollar' },
            { code: 'CNY', name: 'Yuan chinois', nameEn: 'Chinese Yuan' },
            { code: 'INR', name: 'Roupie indienne', nameEn: 'Indian Rupee' },
            { code: 'BRL', name: 'Réal brésilien', nameEn: 'Brazilian Real' },
            { code: 'RUB', name: 'Rouble russe', nameEn: 'Russian Ruble' },
            { code: 'KRW', name: 'Won sud-coréen', nameEn: 'South Korean Won' },
            { code: 'MXN', name: 'Peso mexicain', nameEn: 'Mexican Peso' },
            { code: 'SGD', name: 'Dollar singapourien', nameEn: 'Singapore Dollar' },
            { code: 'HKD', name: 'Dollar de Hong Kong', nameEn: 'Hong Kong Dollar' },
            { code: 'NOK', name: 'Couronne norvégienne', nameEn: 'Norwegian Krone' },
            { code: 'SEK', name: 'Couronne suédoise', nameEn: 'Swedish Krona' },
            { code: 'DKK', name: 'Couronne danoise', nameEn: 'Danish Krone' },
            { code: 'NZD', name: 'Dollar néo-zélandais', nameEn: 'New Zealand Dollar' },
            { code: 'ZAR', name: 'Rand sud-africain', nameEn: 'South African Rand' },
            { code: 'PLN', name: 'Zloty polonais', nameEn: 'Polish Zloty' },
            { code: 'TRY', name: 'Livre turque', nameEn: 'Turkish Lira' },
            { code: 'THB', name: 'Baht thaïlandais', nameEn: 'Thai Baht' },
            { code: 'MAD', name: 'Dirham marocain', nameEn: 'Moroccan Dirham' }
        ];
    }

    /**
     * Rend la page
     * @param {HTMLElement} container - Conteneur
     */
    async render(container) {
        this._container = container;
        const defaultFrom = 'EUR';
        const defaultTo = 'USD';

        container.innerHTML = `
            <div class="page converter-page">
                <div class="converter-container">
                    
                    <div class="card converter-card">
                        <div class="card-body">

                             <!-- Text Result -->
                             <div id="conversion-text-result" class="conversion-text-result">
                                <!-- Will be filled dynamically -->
                             </div>

                            <!-- FROM Row -->
                            <div class="converter-box-row">
                                <input type="number" id="amount-from" class="converter-input" value="1" min="0" step="any" placeholder="0">
                                <div class="converter-select-wrapper">
                                    <select id="currency-from" class="converter-select">
                                        ${this._createCurrencyOptions(defaultFrom)}
                                    </select>
                                </div>
                            </div>
                            
                            <!-- Conversion Rate Info -->
                            <div class="rate-info" id="result-rate">
                                1 ${defaultFrom} = ... ${defaultTo}
                            </div>

                            <!-- TO Row -->
                            <div class="converter-box-row">
                                <input type="number" id="amount-to" class="converter-input" value="..." min="0" step="any" placeholder="0">
                                <div class="converter-select-wrapper">
                                    <select id="currency-to" class="converter-select">
                                        ${this._createCurrencyOptions(defaultTo)}
                                    </select>
                                </div>
                            </div>

                        </div>
                    </div> 
                </div>
            </div>
        `;

        this._setupEventListeners();
        
        // Trigger initial conversion
        setTimeout(() => this._convert('from'), 100);
    }

    /**
     * Crée les options de devises
     * @param {string} selected - Devise sélectionnée
     * @returns {string} HTML
     * @private
     */
    _createCurrencyOptions(selected) {
        const lang = getCurrentLang();
        return this._commonCurrencies.map(c => {
            const name = (lang === 'en' && c.nameEn) ? c.nameEn : c.name;
            return `
            <option value="${c.code}" ${c.code === selected ? 'selected' : ''}>
                ${c.code} - ${name}
            </option>
        `}).join('');
    }

    /**
     * Configure les écouteurs
     * @private
     */
    _setupEventListeners() {
        const fromSelect = this._container.querySelector('#currency-from');
        const toSelect = this._container.querySelector('#currency-to');
        const amountFromInput = this._container.querySelector('#amount-from');
        const amountToInput = this._container.querySelector('#amount-to');

        // Conversion temps réel bidirectionnelle
        let debounceTimer;
        
        const triggerConversion = (source, delay = 150) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                this._convert(source);
            }, delay);
        };

        // Très rapide pour la frappe (surtout si en cache)
        amountFromInput?.addEventListener('input', () => triggerConversion('from', 50));
        amountToInput?.addEventListener('input', () => triggerConversion('to', 50));
        
        // Un peu plus lent pour le changement de devise pour éviter les appels inutiles si on scrolle vite
        fromSelect?.addEventListener('change', () => {
             this._cachedRate = { pair: '', rate: 0, timestamp: 0 }; // Invalider le cache
             triggerConversion('from', 200);
        });
        toSelect?.addEventListener('change', () => {
             this._cachedRate = { pair: '', rate: 0, timestamp: 0 }; // Invalider le cache
             triggerConversion('from', 200);
        });
    }
    /**
     * Effectue la conversion
     * @param {string} source - 'from' ou 'to' (quelle input a été modifiée)
     * @private
     */
    async _convert(source = 'from') {
        const amountFromInput = this._container.querySelector('#amount-from');
        const amountToInput = this._container.querySelector('#amount-to');
        const fromSelect = this._container.querySelector('#currency-from');
        const toSelect = this._container.querySelector('#currency-to');
        const resultRate = this._container.querySelector('#result-rate');
        const textResult = this._container.querySelector('#conversion-text-result');

        // On détermine la conversion à faire
        // Si source == 'from', on fait From -> To. On met à jour amountToInput.
        // Si source == 'to', on fait To -> From. On met à jour amountFromInput.

        const fromCurrency = fromSelect.value;
        const toCurrency = toSelect.value;
        
        let amountToConvert = 0;
        let sourceCurrency = '';
        let targetCurrency = '';

        if (source === 'from') {
             amountToConvert = parseFloat(amountFromInput.value);
             sourceCurrency = fromCurrency;
             targetCurrency = toCurrency;
        } else {
             amountToConvert = parseFloat(amountToInput.value);
             sourceCurrency = toCurrency; // On convertit DEPUIS la "cible" VERS la "source"
             targetCurrency = fromCurrency;
        }

        if (isNaN(amountToConvert)) {
             // Si vide ou invalide
             // On efface l'autre champ
             if (source === 'from') amountToInput.value = '';
             else amountFromInput.value = '';
             return;
        }

        try {
            let result;
            const pairKey = `${sourceCurrency}-${targetCurrency}`;
            
            // Vérification du cache pour éviter l'appel API
            if (this._cachedRate.pair === pairKey && (Date.now() - this._cachedRate.timestamp < 60000)) {
                // Utilisation du cache (valide 1 min)
                const rate = this._cachedRate.rate;
                result = {
                    result: amountToConvert * rate,
                    rate: rate
                };
            } else if (this._cachedRate.pair === `${targetCurrency}-${sourceCurrency}` && (Date.now() - this._cachedRate.timestamp < 60000)) {
                // Utilisation du cache inverse
                const rate = 1 / this._cachedRate.rate;
                result = {
                    result: amountToConvert * rate,
                    rate: rate
                };
            } else {
                // Appel à l'API via le service
                result = await this._services.exchangeRate.convert(amountToConvert, sourceCurrency, targetCurrency);
                
                // Mise à jour du cache
                this._cachedRate = {
                    pair: pairKey,
                    rate: result.rate,
                    timestamp: Date.now()
                };
            }
            
            // Récupération des noms pour le texte
            const fromName = this._commonCurrencies.find(c => c.code === fromCurrency)?.name || fromCurrency;
            const toName = this._commonCurrencies.find(c => c.code === toCurrency)?.name || toCurrency;
            
            // Mise à jour de l'UI
            if (source === 'from') {
                amountToInput.value = result.result.toFixed(2);
                resultRate.textContent = `1 ${fromCurrency} = ${result.rate.toFixed(5)} ${toCurrency}`; 
                
                // Mettre à jour le texte
                // "X From Name égal Y To Name"
                if (textResult) {
                     textResult.innerHTML = `${amountToConvert.toLocaleString('fr-FR')} ${fromName} ${t('equal')}<br><span style="font-weight: 500; font-size: 1.25em;">${result.result.toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ${toName}</span>`;
                }

            } else {
                amountFromInput.value = result.result.toFixed(2);
                // On affiche toujours le taux dans le sens From -> To pour l'affichage principal
                resultRate.textContent = `1 ${toCurrency} = ${result.rate.toFixed(5)} ${fromCurrency}`;
                
                if (textResult) {
                     textResult.innerHTML = `${amountToConvert.toLocaleString('fr-FR')} ${toName} ${t('equal')}<br><span style="font-weight: 500; font-size: 1.25em;">${result.result.toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ${fromName}</span>`;
                }
            }

        } catch (error) {
            console.error('[ConverterPage] Erreur de conversion:', error);
            if (resultRate) resultRate.textContent = 'Erreur réseau';
        }
    }

    /**
     * Détruit la page
     */
    destroy() {
        this._container = null;
    }
}
